import { groupBy, isNil, join, map, sum, sumBy, uniq } from 'lodash';
import invariant from 'tiny-invariant';
import { evaluate, round } from 'mathjs';
import {
  ModelsMap,
  ModelsNameMap,
  ShapeFoldersMap,
  ShapesMap,
  SheetCalibrationsMap,
  SparkelPropertiesMap,
} from '../components/common/ForgeViewer';
import {
  AutoShapeDeepFragment,
  CalculationType,
  ElementIdentifierDbidType,
  ForgeAttributePredicateType,
  ForgeAttributeQueryType,
  ForgeAttributeType,
  PredicateType,
  Quantity,
  QuantityEnum,
  QuantityOperation,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
  SparkelPropertyInfoFragment,
  UnitSettingsType,
} from '../gql/graphql';
import { intersection } from '../utils/dbid-utils';
import { visibleFirstObjects } from '../services/viewer/ForgeContextMenu';
import { convertQuantityToSIUnits } from '../utils/unit-utils';
import { calculateGeometry } from './geometric-operations';
import { isGeometricAlgorithmType } from './geometry/algorithm-types';
import {
  OperationResult,
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
  isGeometricProperty,
  isLocationProperty,
} from './geometry/geometric-types';
import {
  Property,
  PropertyType,
  QuantityWithUnits,
  ResolvedQuantity,
} from './property-types';
import {
  getSparkelCategoryFromIfcClass,
  getSparkelCategoryFromRevitCategory,
} from './category-mapping';

// urn -> propertySet -> (dbId -> propertyValue)
export type SparkelPropertiesIndex = Record<
  string,
  Record<string, Map<number, string>>
>;

// SparkelCategory enum and mapping functions moved to category-mapping.ts

const aggregate = (
  properties: Property[],
  operation: QuantityOperation,
  dbIds: ElementIdentifierDbidType[]
): QuantityWithUnits => {
  switch (operation) {
    case QuantityOperation.Sum: {
      invariant(
        properties.every((property) => property.units === properties[0].units),
        () =>
          `All properties should have equal units. Found ${uniq(
            map(properties, (properties) => properties.units)
          )}`
      );

      return convertQuantityToSIUnits({
        value: sumBy(properties, (property) => {
          if (typeof property.value === 'number') {
            return property.value;
          } else {
            const parsedNumberMatch = property.value.match(
              VALID_NUMBER_STRING_PATTERN
            );
            if (parsedNumberMatch === null) {
              return 0;
            } else {
              return Number.parseFloat(
                // This is okay given the regex
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                parsedNumberMatch.groups!.value.replaceAll(/,| /g, '')
              );
            }
          }
        }),
        units:
          properties[0]?.units
            ?.replaceAll('\u00B2', '^2')
            .replaceAll('\u00B3', '^3') || null,
      });
    }
    case QuantityOperation.Count:
      return {
        value: properties.length,
        units: null,
      };

    case QuantityOperation.Extract: {
      const propertiesByUrn = groupBy(
        properties,
        (property) => property.modelUrn
      );
      const propertiesByUrnAndDbId = Object.fromEntries(
        Object.keys(propertiesByUrn).map((urn) => [
          urn,
          groupBy(propertiesByUrn[urn], (property) => property.dbId),
        ])
      );

      return convertQuantityToSIUnits({
        // return the value of the property for each dbId, or NaN if no property exists
        value: dbIds.reduce((result, { modelUrn, dbIds }) => {
          const propertiesForModel = propertiesByUrnAndDbId[modelUrn] ?? {};
          dbIds.forEach((dbId) => {
            const propertiesForDbId = propertiesForModel[dbId] ?? [];
            if (propertiesForDbId.length === 0) {
              result.push(NaN);
            } else {
              const value = propertiesForDbId[0].value;
              if (typeof value === 'number') {
                result.push(value);
              } else {
                const tokens = value.split(';');
                result.push(join(tokens.sort(), ';'));
              }
            }
          });
          return result;
        }, [] as (number | string)[]),
        units:
          properties[0]?.units
            ?.replaceAll('\u00B2', '^2')
            .replaceAll('\u00B3', '^3') || null,
      });
    }
  }
};

export type SheetShapeWithSheetInfo = SheetShapeDeepFragment & {
  sheet: {
    id: string;
    name: string;
    filename: string;
  };
};

export type SheetShapeWithSheetInfoMap = Record<
  string,
  SheetShapeWithSheetInfo[]
>;

// Precomputed mesh algorithm results from backend processing
// Key is OBJ dbId (use model.reverseMapDbId to convert from viewer dbId)
export type MeshAlgorithmResult = {
  value: number;
  unit: string;
};
export type MeshAlgorithmResultsForElement = Record<
  string,
  MeshAlgorithmResult
>;
// Map from modelUrn to (objDbId -> algorithmResults)
export type MeshAlgorithmResultsMap = Record<
  string,
  Record<string, MeshAlgorithmResultsForElement>
>;

export interface PropertyContext {
  loadedModels: ModelsMap;
  loadedShapes: ShapesMap;
  loadedSheetShapes: SheetShapeWithSheetInfoMap;
  loadedSheetCalibrations: SheetCalibrationsMap;
  loadedShapeFolders: ShapeFoldersMap;
  loadedAutoShapes: AutoShapeDeepFragment[];
  loadedSparkelProperties: SparkelPropertiesMap;
  loadedActiveModelsNameMap: ModelsNameMap;
  loadedSheets: Array<{
    id: string;
    name: string;
    filename: string;
    version?: number;
  }>;
  // Optional pre-indexed view for fast lookups: urn -> propertySet -> (dbId -> value)
  loadedSparkelPropertiesIndex?: SparkelPropertiesIndex;
  // Optional precomputed mesh algorithm results from backend processing
  meshAlgorithmResults?: MeshAlgorithmResultsMap;
}

export const resolveQuantity = async (
  context: PropertyContext,
  modelDbIds: ElementIdentifierDbidType[],
  quantity: Omit<Quantity, '__typename'>,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  },
  derivedFormulaScope?: Map<string, ResolvedQuantity['value']>
): Promise<ResolvedQuantity> => {
  switch (quantity.type) {
    case QuantityEnum.Static:
      if (!isNil(quantity.value)) {
        return {
          value: quantity.value,
          units: null,
        };
      } else if (!isNil(quantity.stringValue)) {
        return {
          value: quantity.stringValue,
          units: null,
        };
      } else {
        return {
          value: 0, // Todo: consider returning null instead
          units: null,
        };
      }

    case QuantityEnum.Dynamic:
      invariant(
        quantity.calculation,
        'Calculation must be set for dynamic quantity'
      );
      return resolveDynamicQuantity(
        context,
        modelDbIds,
        quantity.calculation,
        projectSettings
      );

    case QuantityEnum.Derived:
      invariant(quantity.derivedFormula, 'Formula is missing');
      try {
        const derivedValue: number | string = evaluate(
          quantity.derivedFormula,
          derivedFormulaScope
        );
        if (typeof derivedValue === 'number') {
          return {
            value: round(derivedValue, 2),
            units: null, // todo: consider carring units in the formula
          };
        } else {
          return {
            value: derivedValue,
            units: null,
          };
        }
      } catch (err) {
        return {
          value: NaN,
          units: null,
        };
      }

    case QuantityEnum.DatabaseLookup:
      // Database lookup is resolved separately via GraphQL query
      // Return a placeholder value for now - actual resolution happens in the component
      return {
        value: '',
        units: null,
      };
  }
};

// Inspired by https://regexr.com/38sbo and tweaked to include unit postfix
export const VALID_NUMBER_STRING_PATTERN =
  /^(?<value>-?\d+(,| \d{3})*(\.\d+)?)(?<unit> [\^\w]+)?$/;
export const resolveDynamicQuantity = async (
  context: PropertyContext,
  modelDbIds: ElementIdentifierDbidType[],
  calculation: CalculationType,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): Promise<ResolvedQuantity> => {
  const { operation, attribute } = calculation;
  const totalDbIds = sum(map(modelDbIds, ({ dbIds }) => dbIds.length));

  // COUNT special case: No need to select properties as the result is the length of the input dbIds
  if (operation === QuantityOperation.Count) {
    return {
      value: totalDbIds,
      units: null,
    };
  }

  // Early return for other operations when there are 0 elements
  // No point calling resolveProperties when there's nothing to aggregate
  if (totalDbIds === 0) {
    return {
      value: 0,
      units: null,
      dataPoints: [],
    };
  }

  invariant(attribute, 'Attribute must be set when extracting values');
  const properties = await resolveProperties(
    context,
    projectSettings,
    attribute,
    modelDbIds
  );
  const result = aggregate(properties, operation, modelDbIds);
  return {
    ...result,
    dataPoints: properties,
  };
};

// Returns all dbids in models matching the given query
export const resolveElementIdentifiersDBID = async (
  context: PropertyContext,
  query: ForgeAttributeQueryType,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): Promise<ElementIdentifierDbidType[]> => {
  let result: ElementIdentifierDbidType[] = [];
  //Always choose geometric properties last
  const geometricPredicates = query.predicates.filter(
    (predicate) =>
      predicate.attribute.category === SPARKEL_GEOMETRIC_PROPERTY_SET ||
      predicate.attribute.category === SPARKEL_LOCATION_PROPERTY_SET
  );

  const otherPredicates = query.predicates.filter(
    (predicate) =>
      predicate.attribute.category !== SPARKEL_GEOMETRIC_PROPERTY_SET &&
      predicate.attribute.category !== SPARKEL_LOCATION_PROPERTY_SET
  );

  // Combine the sorted geometric predicates with the original order of other predicates
  const sortedPredicates = otherPredicates.concat(geometricPredicates);

  for (const predicate of sortedPredicates) {
    const properties = await resolveProperties(
      context,
      projectSettings,
      // For NOT_EXISTS and NOT_CONTAINS, we want properties that are missing the attribute
      predicate.predicate === PredicateType.NotExists ||
        predicate.predicate === PredicateType.NotContains
        ? undefined
        : predicate.attribute,
      // We only need to consider the remaining dbIds
      result.length === 0 ? undefined : result
    );
    const matchingDbIds = dbIdsMatchingPredicate(properties, predicate);
    // Initial pass, set properties to all dbIds matching predicate
    if (result.length === 0) {
      result = matchingDbIds;
    } else {
      // Narrow properties by dbIds also matching current predicate
      result = intersection(result, matchingDbIds);
    }
    // No dbIds match all predicates, we can stop
    if (result.length === 0) {
      break;
    }
  }
  return result;
};

enum AutodeskValueType {
  Boolean = 1,
  Number = 3,
  Text = 20,
}

function getPropertyType(autodeskType: number): PropertyType {
  switch (autodeskType) {
    case AutodeskValueType.Boolean:
      return PropertyType.Boolean;
    case AutodeskValueType.Number:
      return PropertyType.Number;
    case AutodeskValueType.Text:
      return PropertyType.Text;
    default:
      return PropertyType.Unknown;
  }
}

export function toPropertyArray(
  propertyResult: Autodesk.Viewing.PropertyResult,
  modelUrn: string
): Property[] {
  // Create properties from the properties array
  const propertiesFromArray = propertyResult.properties.map(
    (autodeskProperty): Property =>
      ({
        attribute: {
          category: autodeskProperty.displayCategory,
          name: autodeskProperty.attributeName,
        },
        value: autodeskProperty.displayValue,
        type: getPropertyType(autodeskProperty.type),
        units: autodeskProperty.units,
        dbId: propertyResult.dbId,
        modelUrn,
      } as Property)
  );

  const additionalProperties: Property[] = [];

  // Add externalId property if it exists
  if (
    propertyResult.externalId !== undefined &&
    propertyResult.externalId !== null
  ) {
    const externalIdProperty: Property = {
      attribute: {
        category: 'Element',
        name: 'Element GUID',
      },
      value: propertyResult.externalId,
      type: PropertyType.Text,
      units: null,
      dbId: propertyResult.dbId,
      modelUrn,
    };

    additionalProperties.push(externalIdProperty);
  }

  const ifcCategory = propertyResult.properties.find(
    (prop) =>
      prop.displayCategory === 'Element' && prop.attributeName === 'IfcClass'
  )?.displayValue;
  const revitCategory = propertyResult.properties.find(
    (prop) =>
      prop.displayCategory === '__category__' &&
      prop.attributeName === 'Category'
  )?.displayValue;

  if (ifcCategory) {
    const sparkelCategoryProperty: Property = {
      attribute: {
        category: 'Element',
        name: 'Sparkel Category',
      },
      value: getSparkelCategoryFromIfcClass(ifcCategory.toString()),
      type: PropertyType.Text,
      units: null,
      dbId: propertyResult.dbId,
      modelUrn,
    };
    additionalProperties.push(sparkelCategoryProperty);
  } else if (revitCategory) {
    const sparkelCategoryProperty: Property = {
      attribute: {
        category: 'Element',
        name: 'Sparkel Category',
      },
      value: getSparkelCategoryFromRevitCategory(revitCategory.toString()),
      type: PropertyType.Text,
      units: null,
      dbId: propertyResult.dbId,
      modelUrn,
    };
    additionalProperties.push(sparkelCategoryProperty);
  }

  return [...propertiesFromArray, ...additionalProperties];
}

// getSparkelCategoryFromIfcClass and getSparkelCategoryFromRevitCategory
// moved to category-mapping.ts and exported from there

// Note: this modifies the property in place
function convertModelName(
  property: Property,
  loadedActiveModelsNameMap: ModelsNameMap
): void {
  // APS Data Management returns the property as {category: "Item", name: "Source file"} (lowercase "f")
  // We use case-insensitive comparison to handle both "Source File" and "Source file"
  // The map keys are lowercased, so we lowercase the property value for lookup
  if (
    property.attribute.name.toLowerCase() === 'source file' &&
    typeof property.value === 'string'
  ) {
    try {
      const lookupKey = property.value.toLowerCase();
      let mappedValue = loadedActiveModelsNameMap[lookupKey];

      // Fallback for cloned projects: if the model ID doesn't exist (because the APS model
      // was cloned from another project), try looking up by the model's URN instead.
      // The map also contains forgeUrn -> filename entries for this purpose.
      if (!mappedValue && property.modelUrn) {
        mappedValue = loadedActiveModelsNameMap[property.modelUrn];
      }

      if (mappedValue) property.value = mappedValue;
    } catch (err) {
      //Ignore and dont change name if not found in map
    }
  }
}

function dbIdsMatchingPredicate(
  properties: Property[],
  predicate: ForgeAttributePredicateType
): ElementIdentifierDbidType[] {
  const propertiesByModel = groupBy(
    properties,
    (property) => property.modelUrn
  );
  return Object.keys(propertiesByModel)
    .map((modelUrn) => {
      const propertiesForModel = propertiesByModel[modelUrn];
      const propertiesForDbId = groupBy(
        propertiesForModel,
        (property) => property.dbId
      );

      const matchingDbIds = Object.values(propertiesForDbId)
        .filter((properties) => propertiesPredicate(properties, predicate))
        // Grouped by dbId, so every element will have the same dbId
        .map((properties) => properties[0].dbId);
      return { modelUrn, dbIds: matchingDbIds } as ElementIdentifierDbidType;
    })
    .filter((modelDbId) => modelDbId.dbIds.length > 0);
}

export function createRegexFromInputString(input: string) {
  try {
    // Check if the input is of the form `/<pattern>/<flags>`
    const match = input.match(/^\/(.*)\/([a-z]*)$/);
    // We need to protect ourselves from ReDos; though this just happens on the F.E so not unless the user ReDos themself
    // Would be particularly important if we were parsing this in the B.E. We do not anticipate that users are doing crazy backtracking
    let pattern = input,
      flags = '';

    if (match) {
      pattern = match[1];
      flags = match[2];
    }
    return new RegExp(pattern, flags);
  } catch (e) {
    console.warn('Error creating regex', e);
    return new RegExp('a^');
  }
}

// eslint-disable-next-line complexity
function propertiesPredicate(
  propertiesForDbId: Property[],
  predicate: ForgeAttributePredicateType
): boolean {
  // In most cases there's only a single attribute (e.g. "Name" EQUALS "foo")
  const predicateValue = predicate.attributeValues?.[0] ?? '';

  const isAttributeEqual = (property: Property) =>
    property.attribute.category === predicate.attribute.category &&
    property.attribute.name === predicate.attribute.name;

  const isPredicateValueEqual = (property: Property) => {
    if (typeof property.value === 'number') {
      return (
        Math.abs(property.value - Number.parseFloat(predicateValue)) < 0.01
      );
    }
    return property.value === predicateValue;
  };

  const doesPredicateValueMatchRegex = (property: Property) => {
    const regex = createRegexFromInputString(predicateValue);
    return regex.test(property.value.toString());
  };

  const somePropertyMatches = (predicate: (property: Property) => boolean) => {
    return propertiesForDbId.some(
      (property) => isAttributeEqual(property) && predicate(property)
    );
  };

  const doesNotContainPredicateValue = (property: Property) => {
    if (
      typeof property.value === 'string' ||
      typeof property.value === 'number'
    ) {
      return !property.value
        .toString()
        .toLowerCase()
        .includes(predicateValue.toLowerCase());
    }
    return true;
  };

  switch (predicate.predicate) {
    case PredicateType.Equals:
      return somePropertyMatches((property) => isPredicateValueEqual(property));
    case PredicateType.NotEquals:
      return somePropertyMatches(
        (property) => !isPredicateValueEqual(property)
      );
    case PredicateType.Contains:
      return somePropertyMatches((property) =>
        property.value
          .toString()
          .toLowerCase()
          .includes(predicateValue.toLowerCase())
      );
    case PredicateType.NotContains:
      // Check if there are no properties matching the attribute or if all properties that match do not contain the predicate value.
      return propertiesForDbId.every(
        (property) =>
          !isAttributeEqual(property) || doesNotContainPredicateValue(property)
      );
    case PredicateType.Exists:
      return propertiesForDbId.some((property) => isAttributeEqual(property));
    case PredicateType.NotExists:
      // Note the use of every. For NOT_EXISTS we have to check that there are no properties for the dbId with the given attribute
      return propertiesForDbId.every((property) => !isAttributeEqual(property));
    case PredicateType.GreaterThan:
      return somePropertyMatches(
        (property) =>
          (typeof property.value === 'string'
            ? Number.parseFloat(property.value)
            : property.value) > Number.parseFloat(predicateValue)
      );
    case PredicateType.LessThan:
      return somePropertyMatches(
        (property) =>
          (typeof property.value === 'string'
            ? Number.parseFloat(property.value)
            : property.value) < Number.parseFloat(predicateValue)
      );

    case PredicateType.FromRegex:
      return somePropertyMatches(doesPredicateValueMatchRegex);

    case PredicateType.AnyOf:
      return somePropertyMatches((property) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        predicate.attributeValues!.includes(String(property.value))
      );
  }
}

export const resolveProperties = async (
  context: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  attribute?: ForgeAttributeType,
  dbIds?: ElementIdentifierDbidType[]
): Promise<Property[]> => {
  // Early return if dbIds is provided but has zero total elements
  if (dbIds !== undefined) {
    const totalDbIds = dbIds.reduce((acc, id) => acc + id.dbIds.length, 0);
    if (dbIds.length === 0 || totalDbIds === 0) {
      return [];
    }
  }
  const modelProperties = await resolveModelProperties(
    context,
    dbIds,
    attribute
  );

  let geometricProperties: Property[] = [];
  if (dbIds)
    geometricProperties = await resolveGeometricProperties(
      context,
      dbIds,
      projectSettings,
      attribute
    );

  const shapeProperties = resolveShapeProperties(context, dbIds, attribute);
  const sparkelProperties: Property[] = resolveSparkelProperties(
    context,
    dbIds,
    attribute
  );

  return [
    ...modelProperties,
    ...shapeProperties,
    ...sparkelProperties,
    ...geometricProperties,
  ];
};

// eslint-disable-next-line complexity
async function resolveModelProperties(
  context: PropertyContext,
  dbIds?: ElementIdentifierDbidType[],
  attribute?: ForgeAttributeType
) {
  // Early return if dbIds is provided but empty - no point calling Forge API
  if (dbIds !== undefined && dbIds.length === 0) {
    return [];
  }

  // Also check if all dbIds arrays are empty
  if (dbIds !== undefined) {
    const totalDbIds = dbIds.reduce((acc, id) => acc + id.dbIds.length, 0);
    if (totalDbIds === 0) {
      return [];
    }
  }

  let properties: Property[] = [];

  for (const [modelUrn, model] of Object.entries(context.loadedModels)) {
    let dbIdFilter: number[] = [];
    if (dbIds) {
      const dbIdsForModel = dbIds.find(
        (dbId) => dbId.modelUrn === modelUrn
      )?.dbIds;
      if (!dbIdsForModel || dbIdsForModel.length === 0) {
        continue;
      }
      dbIdFilter = dbIdsForModel;
    }

    const autodeskProperties = await new Promise<
      Autodesk.Viewing.PropertyResult[]
    >((resolve, reject) => {
      const isElementGuid =
        attribute?.name === 'Element GUID' && attribute?.category === 'Element';

      const isSparkelCategory =
        attribute?.name === 'Sparkel Category' &&
        attribute?.category === 'Element';

      let options: Autodesk.Viewing.PropertyOptions = {
        propFilter: attribute ? [attribute.name] : undefined,
        categoryFilter: attribute ? [attribute.category] : undefined,
      };

      if (isSparkelCategory) {
        // When resolving the synthetic Sparkel Category, we need both sources:
        // - Revit: '__category__' / 'Category'
        // - IFC: 'Element' / 'IfcClass'
        // Fetch both so that toPropertyArray can synthesize the Sparkel Category consistently.
        options.propFilter = ['Category', 'IfcClass'];
        options.categoryFilter = undefined; // allow pulling from multiple categories
      } else if (isElementGuid) {
        options.propFilter = ['externalId'];
      }

      model.getBulkProperties2(
        dbIdFilter,
        options,
        (res) => resolve(res),
        () => {
          return void reject('Failed to get properties');
        }
      );
    });
    const propertiesForModel = autodeskProperties
      .flatMap((propertyResult) => toPropertyArray(propertyResult, modelUrn))
      .filter((prop) => {
        // Only keep properties that have a category
        if (!prop.attribute.category) return false;
        // If a specific attribute is requested, only return that attribute
        // Use case-insensitive comparison for name since APS may return different casing
        // (e.g., "Source file" vs "Source File")
        if (attribute) {
          return (
            prop.attribute.category === attribute.category &&
            prop.attribute.name.toLowerCase() === attribute.name.toLowerCase()
          );
        }
        return true;
      });
    propertiesForModel.forEach(convertQuantityToSIUnits);
    propertiesForModel.forEach((prop) =>
      convertModelName(prop, context.loadedActiveModelsNameMap)
    );
    properties = properties.concat(propertiesForModel);
  }
  return properties;
}

// eslint-disable-next-line complexity
export function resolveSparkelProperties(
  context: PropertyContext,
  dbIds?: ElementIdentifierDbidType[],
  attribute?: ForgeAttributeType
) {
  const properties: Property[] = [];

  // If we have an index, use it for O(1) lookups per dbId/propertySet
  const index = context.loadedSparkelPropertiesIndex;
  if (index) {
    // Build a fast dbId filter per urn
    const dbIdFilterByUrn: Record<string, Set<number>> = {};
    if (dbIds) {
      for (const { modelUrn, dbIds: ids } of dbIds) {
        if (ids && ids.length > 0) {
          dbIdFilterByUrn[modelUrn] = new Set<number>(ids);
        }
      }
    }

    const handlePush = (
      modelUrn: string,
      dbId: number,
      propertySetName: string,
      propertyValue: string
    ) => {
      if (propertySetName === 'IfcDistributionSystem') {
        propertyValue.split(';').forEach((system) => {
          properties.push({
            modelUrn,
            dbId,
            attribute: { category: sparkelPropertySet, name: propertySetName },
            value: system.trim(),
            type: PropertyType.Text,
            units: null,
          });
        });
      } else {
        properties.push({
          modelUrn,
          dbId,
          attribute: { category: sparkelPropertySet, name: propertySetName },
          value: propertyValue,
          type: PropertyType.Text,
          units: null,
        });
      }
    };

    for (const [modelUrn, bySet] of Object.entries(index)) {
      const allowed = dbIdFilterByUrn[modelUrn];
      // If a dbId filter was provided, skip models not included in the filter entirely
      if (dbIds && !allowed) {
        continue;
      }

      if (attribute && attribute.category === sparkelPropertySet) {
        const mapForSet = bySet[attribute.name];
        if (!mapForSet || !(mapForSet instanceof Map)) continue;
        for (const [dbId, value] of mapForSet) {
          if (!dbIds || (allowed && allowed.has(dbId))) {
            handlePush(modelUrn, dbId, attribute.name, value);
          }
        }
      } else if (!attribute) {
        // Return all sparkel properties (respecting dbId filter when present)
        for (const [propertySetName, mapForSet] of Object.entries(bySet)) {
          // Looks like the mapForSet can either be a map or an object.
          // It also seems like the said object is always empty;
          // Also this only occurs in history mode,in which case, we are going to ingore this
          if (!(mapForSet instanceof Map)) continue;
          for (const [dbId, value] of mapForSet) {
            if (!dbIds || (allowed && allowed.has(dbId))) {
              handlePush(modelUrn, dbId, propertySetName, value);
            }
          }
        }
      }
    }

    return properties;
  }

  // Normalize possibly inconsistent historical shapes where each entry can be
  // an array or an object map (id -> SparkelPropertyInfoFragment)
  const parseJson = (v: unknown): unknown => {
    if (typeof v === 'string') {
      try {
        return parseJson(JSON.parse(v));
      } catch {
        return v;
      }
    }
    if (Array.isArray(v)) return v.map(parseJson);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) out[k] = parseJson(val);
      return out;
    }
    return v;
  };

  const toArray = (v: unknown): SparkelPropertyInfoFragment[] => {
    const parsed = parseJson(v);
    if (Array.isArray(parsed)) return parsed as SparkelPropertyInfoFragment[];
    if (parsed && typeof parsed === 'object')
      return Object.values(
        parsed as Record<string, unknown>
      ) as SparkelPropertyInfoFragment[];
    return [];
  };

  for (const [modelUrn, rawSparkelAttributes] of [
    ...Object.entries(context.loadedSparkelProperties || {}),
  ]) {
    const sparkelAttributes = toArray(rawSparkelAttributes);
    let dbIdFilter: number[] = [];
    if (dbIds) {
      const dbIdsForModel = dbIds.find(
        (dbId) => dbId.modelUrn === modelUrn
      )?.dbIds;
      if (!dbIdsForModel || dbIdsForModel.length === 0) {
        continue;
      }
      dbIdFilter = dbIdsForModel;
    }

    if (!attribute || attribute.category === sparkelPropertySet) {
      const dbIdSet = new Set<number>(dbIdFilter);
      sparkelAttributes.forEach((sparkelProperty) => {
        if (
          (dbIdFilter.length === 0 || dbIdSet.has(sparkelProperty.dbId)) &&
          (!attribute || attribute.name === sparkelProperty.propertySet)
        ) {
          if (sparkelProperty.propertySet === 'IfcDistributionSystem') {
            // Split system values and create property for each
            sparkelProperty.propertyValue
              .split(';')
              .forEach((system: string) => {
                properties.push({
                  modelUrn: sparkelProperty.modelUrn,
                  dbId: sparkelProperty.dbId,
                  attribute: {
                    category: sparkelPropertySet,
                    name: sparkelProperty.propertySet,
                  },
                  value: system.trim(), // Added trim() to handle potential whitespace
                  type: PropertyType.Text,
                  units: null,
                });
              });
          } else {
            // Handle non-system properties as before
            properties.push({
              modelUrn: sparkelProperty.modelUrn,
              dbId: sparkelProperty.dbId,
              attribute: {
                category: sparkelPropertySet,
                name: sparkelProperty.propertySet,
              },
              value: sparkelProperty.propertyValue,
              type: PropertyType.Text,
              units: null,
            });
          }
        }
      });
    }
  }

  return properties;
}

function resolveShapeProperties(
  context: PropertyContext,
  dbIds?: ElementIdentifierDbidType[],
  attribute?: ForgeAttributeType
) {
  const properties: Property[] = [];

  for (const [modelUrn, shapes] of [
    ...Object.entries(context.loadedShapes),
    ...Object.entries(context.loadedSheetShapes),
  ]) {
    let dbIdFilter: number[] = [];
    if (dbIds) {
      const dbIdsForModel = dbIds.find(
        (dbId) => dbId.modelUrn === modelUrn
      )?.dbIds;
      if (!dbIdsForModel || dbIdsForModel.length === 0) {
        continue;
      }
      dbIdFilter = dbIdsForModel;
    }

    const propertiesForShape: Property[] = (
      shapes as (SheetShapeWithSheetInfo | ShapeDeepFragment)[]
    )
      .filter(
        (shape) => dbIdFilter.length === 0 || dbIdFilter.includes(shape.dbId)
      )
      .flatMap((shape) =>
        allPropertiesForShape(shape, modelUrn, [
          ...Object.values(context.loadedShapeFolders).flatMap(
            (folders) => folders
          ),
          ...Object.values(context.loadedAutoShapes).flatMap(
            (autoShapes) => autoShapes
          ),
        ])
      )
      .filter(
        (property) =>
          !attribute ||
          (attribute.category === property.attribute.category &&
            attribute.name === property.attribute.name)
      );
    properties.push(...propertiesForShape);
  }
  return properties;
}

function allPropertiesForShape(
  shape: ShapeDeepFragment | SheetShapeWithSheetInfo,
  modelUrn: string,
  shapeFolders: (ShapeFolderDeepFragment | AutoShapeDeepFragment)[]
): Property[] {
  const properties: Property[] = [
    {
      modelUrn,
      dbId: shape.dbId,
      attribute: { category: 'Element', name: 'Element GUID' },
      value: shape.id,
      type: PropertyType.Text,
      units: null,
    },
    {
      modelUrn,
      dbId: shape.dbId,
      attribute: { category: 'Element', name: 'Sparkel Category' },
      value: 'Shape',
      type: PropertyType.Text,
      units: null,
    },
    {
      modelUrn,
      dbId: shape.dbId,
      attribute: { category: shapesPropertySet, name: ShapesProperties.NAME },
      value: shape.name,
      type: PropertyType.Text,
      units: null,
    },
    {
      modelUrn,
      dbId: shape.dbId,
      attribute: { category: shapesPropertySet, name: ShapesProperties.TYPE },
      value: getShapeTypeProperty(shape),
      type: PropertyType.Text,
      units: null,
    },
  ];
  const shapeFolder = getShapeFolderName(shape, shapeFolders);
  if (shapeFolder) {
    properties.push({
      modelUrn,
      dbId: shape.dbId,
      attribute: { category: shapesPropertySet, name: ShapesProperties.FOLDER },
      value: shapeFolder,
      type: PropertyType.Text,
      units: null,
    });
  }
  if (shape.__typename === 'SheetShape') {
    properties.push({
      modelUrn,
      dbId: shape.dbId,
      attribute: {
        category: shapesPropertySet,
        name: ShapesProperties.SOURCE_FILE,
      },
      value: shape.sheet.filename,
      type: PropertyType.Text,
      units: null,
    });
    properties.push({
      modelUrn,
      dbId: shape.dbId,
      attribute: {
        category: shapesPropertySet,
        name: ShapesProperties.PAGE_NUMBER,
      },
      value: shape.sheetPageNumber,
      type: PropertyType.Number,
      units: null,
    });

    // Add linked table name for legend shapes
    if (shape.sheetShapeLegend?.legendOrder?.name) {
      properties.push({
        modelUrn,
        dbId: shape.dbId,
        attribute: {
          category: shapesPropertySet,
          name: ShapesProperties.LINKED_TABLE,
        },
        value: shape.sheetShapeLegend.legendOrder.name,
        type: PropertyType.Text,
        units: null,
      });
    }
  }
  return properties;
}

export const shapesPropertySet = 'Shape';

export enum ShapesProperties {
  NAME = 'Name',
  TYPE = 'Type',
  FOLDER = 'Folder',
  SOURCE_FILE = 'Source File',
  PAGE_NUMBER = 'Page Number',
  LINKED_TABLE = 'Linked Table',
}

export enum ShapeType {
  POLYGON = 'Polygon',
  EXTRUDED_POLYGON = 'Volumetric Shape',
  LINE = 'Line',
  POINT = 'Point',
  TAPERED_INSULATION = 'Tapered Insulation',
  LEGEND = 'Legend',
}

export const sparkelPropertySet = 'Sparkel Attributes';

const resolveGeometricProperties = async (
  context: PropertyContext,
  dbIds: ElementIdentifierDbidType[],
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  attribute?: ForgeAttributeType
): Promise<Property[]> => {
  if (!attribute) {
    // Selecting all geometric properties is not supported yet
    return [];
  }
  if (!dbIds) {
    //  Selecting geometric properties for all dbIds is not supported yet
    return [];
  }
  // Early return if dbIds is empty or all dbId arrays are empty
  const totalDbIds = dbIds.reduce((acc, id) => acc + id.dbIds.length, 0);
  if (totalDbIds === 0) {
    return [];
  }
  if (
    attribute.category !== SPARKEL_GEOMETRIC_PROPERTY_SET &&
    attribute.category !== SPARKEL_LOCATION_PROPERTY_SET &&
    attribute.category !== 'Quantities'
  ) {
    return [];
  }

  if (
    isGeometricProperty(attribute.name) ||
    isLocationProperty(attribute.name) ||
    isGeometricAlgorithmType(attribute.name)
  ) {
    const operationResult = await calculateGeometry(
      attribute.name,
      dbIds,
      context,
      projectSettings
    );
    return operationResult
      .map((result) => mapGeometricResultToProperty(result, attribute.name))
      .filter((property) => !isNil(property)) as Property[];
  } else {
    throw new Error('Expected geometric propertytype or algorithmtype');
  }
};

const mapGeometricResultToProperty = (
  [result, context]: OperationResult,
  attributeName: string
): Property | undefined => {
  if (result === undefined) {
    return undefined;
  }
  let category = SPARKEL_GEOMETRIC_PROPERTY_SET;
  if (isLocationProperty(attributeName)) {
    category = SPARKEL_LOCATION_PROPERTY_SET;
  }
  return {
    ...result,
    type: PropertyType.Number,
    modelUrn: context.modelUrn,
    dbId: context.dbId,
    attribute: {
      category,
      name: attributeName,
    },
  };
};

// Use the same property value for both 3D and sheet shapes, e.g. both SheetShapePolygon and ShapePolygon will have the property value "Polygon"
function getShapeTypeProperty(
  shape: ShapeDeepFragment | SheetShapeDeepFragment
) {
  if (shape.__typename === 'SheetShape') {
    if (shape.sheetShapePolygon) {
      return ShapeType.POLYGON;
    } else if (shape.sheetShapeLine) {
      return ShapeType.LINE;
    } else if (shape.sheetShapeSplitLine) {
      return ShapeType.TAPERED_INSULATION;
    } else if (shape.sheetShapeLegend) {
      return ShapeType.LEGEND;
    } else {
      return ShapeType.POINT;
    }
  } else {
    if (shape.polygon) {
      return ShapeType.POLYGON;
    } else if (shape.extrudedPolygon) {
      return ShapeType.EXTRUDED_POLYGON;
    } else if (shape.line) {
      return ShapeType.LINE;
    } else {
      return ShapeType.POINT;
    }
  }
}

function getShapeFolderName(
  shape: ShapeDeepFragment | SheetShapeDeepFragment,
  folders: (ShapeFolderDeepFragment | AutoShapeDeepFragment)[]
) {
  if (shape.autoShapeId) {
    return folders.find((folder) => folder.id === shape.autoShapeId)?.name;
  }
  return folders.find((folder) => folder.id === shape.folder)?.name;
}

export async function getPropertyByDbId(
  model: Autodesk.Viewing.Model,
  dbId: number,
  attributeName: string
): Promise<string | null> {
  async function findProperty(dbId: number): Promise<string | null> {
    return new Promise((resolve, reject) => {
      model.getProperties(
        dbId,
        async (props) => {
          const categoryProp = props.properties.find(
            (prop) => prop.attributeName === attributeName
          );
          if (categoryProp) {
            resolve(categoryProp.displayValue.toString());
          } else {
            // Look for parent property
            const parentProp = props.properties.find(
              (prop) => prop.attributeName === 'parent'
            );
            if (parentProp && parentProp.displayValue) {
              const parentId = parseInt(parentProp.displayValue as string);
              if (!isNaN(parentId) && parentId !== dbId) {
                // Check to avoid infinite loop
                const parentCategory = await findProperty(parentId);
                resolve(parentCategory);
              } else {
                resolve(null); // No valid parent ID found, or same as dbId
              }
            } else {
              resolve(null); // No parent property found
            }
          }
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  return await findProperty(dbId);
}

export async function getParentDbId(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number> {
  return new Promise((resolve) => {
    model.getProperties(dbId, async (props) => {
      const parentProp = props.properties.find(
        (prop) => prop.attributeName === 'parent'
      );
      if (parentProp && parentProp.displayValue) {
        const parentId = parseInt(parentProp.displayValue as string);
        if (!isNaN(parentId)) {
          resolve(parentId);
        } else {
          resolve(0); // No valid parent ID found, or same as dbId
        }
      } else {
        resolve(0); // No parent property found
      }
    });
  });
}

export async function getAllChildrenDbIds(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number[]> {
  const children: number[] = [dbId];

  return new Promise((resolve) => {
    model.getData().instanceTree.enumNodeChildren(dbId, (childId: number) => {
      children.push(childId);
    });
    resolve(children);
  });
}

async function getAllDescendantsDbIds(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number[]> {
  const descendants: number[] = [dbId];

  return new Promise((resolve) => {
    const instanceTree = model.getData().instanceTree;
    const collectDescendants = (parentId: number) => {
      instanceTree.enumNodeChildren(parentId, (childId: number) => {
        descendants.push(childId);
        collectDescendants(childId);
      });
    };
    collectDescendants(dbId);
    resolve(descendants);
  });
}

async function getFirstObjectDbId(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number> {
  const visibleFirstObjectDbIds = visibleFirstObjects(model);

  if (visibleFirstObjectDbIds.has(dbId)) {
    return dbId;
  }

  const parentDbId = await getParentDbId(model, dbId);
  return getFirstObjectDbId(model, parentDbId);
}

export async function getAllRelatedDbIds(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number[]> {
  const firstObject = await getFirstObjectDbId(model, dbId);

  // Get all children of the first object (as before)
  const children = await getAllChildrenDbIds(model, firstObject);

  // Also include siblings and their descendants: if the first object has a parent,
  // get all descendants of the parent to catch multi-part assemblies like slabs
  // where parts might be nested at different levels
  const parentDbId = await getParentDbId(model, firstObject);
  if (parentDbId !== 0) {
    const allDescendantsOfParent = await getAllDescendantsDbIds(
      model,
      parentDbId
    );
    // Combine and deduplicate
    const allRelated = new Set([...children, ...allDescendantsOfParent]);
    return Array.from(allRelated);
  }

  return children;
}

// MagicPolygon-specific variant: we only care about the element itself and its
// descendants, not all descendants of its parent assembly. Using the broader
// set would cause us to ignore completely unrelated siblings that we still
// want to consider as potential colliders.
export async function getAllRelatedDbIdsForMagicPolygon(
  model: Autodesk.Viewing.Model,
  dbId: number
): Promise<number[]> {
  const firstObject = await getFirstObjectDbId(model, dbId);
  return getAllChildrenDbIds(model, firstObject);
}
